import React from "react"
import { useParams } from "react-router-dom"
import { useWebsiteProducts } from "@/hooks/useWebsiteProducts"
import { useCart } from "@/hooks/useCart"
import { useNavigate } from "react-router-dom"
import { ShoppingCart } from "lucide-react"
import { toast } from "sonner"

// Fallback placeholder image
const placeholderImage = "https://via.placeholder.com/400x400?text=No+Image"

const getProductImage = (product: any) => {
  // Use thumbnail if available, otherwise first image, otherwise placeholder
  if (product?.thumbnail?.url) {
    return product.thumbnail.url
  }
  if (product?.images && product.images.length > 0 && product.images[0]?.url) {
    return product.images[0].url
  }
  return placeholderImage
}

export const TagProducts: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const tagId = id ? parseInt(id) : undefined
  const { data: productsData, isLoading } = useWebsiteProducts({
    tag_id: tagId,
    per_page: 20,
  })
  const { addItem, isAdding } = useCart()
  const navigate = useNavigate()
  const token = localStorage.getItem("token")

  // ApiResponse wraps in 'data', and paginated ResourceCollection also wraps in 'data'
  // So for paginated collections: response.data.data
  const products = (productsData as any)?.data?.data || (productsData as any)?.data || []

  const handleAddToCart = (product: any) => {
    if (!token) {
      toast.error("Please login to add items to cart")
      navigate("/website/login")
      return
    }

    addItem({
      product_id: product.id,
      quantity: 1,
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Products</h2>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Loading products...</div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500">No products available</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product: any) => (
              <div
                key={product.id}
                className="group relative cursor-pointer"
                onClick={() => navigate(`/website/product/${product.id}`)}
              >
                <div className="h-[230px] aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-lg bg-gray-200 xl:aspect-w-7 xl:aspect-h-8">
                  <img
                    src={getProductImage(product)}
                    alt={product.name}
                    className="h-full w-full object-cover object-center group-hover:opacity-75 transition"
                    onError={(e) => {
                      e.currentTarget.src = placeholderImage
                    }}
                  />
                </div>
                <h3 className="mt-4 text-sm text-gray-700 hover:text-indigo-600">
                  {product.name}
                </h3>
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-lg font-medium text-gray-900">
                    ${product.sell_price?.toFixed(2) || "0.00"}
                  </p>
                  {product.stock > 0 ? (
                    <span className="text-xs text-green-600">In Stock</span>
                  ) : (
                    <span className="text-xs text-red-600">Out of Stock</span>
                  )}
                </div>
                {product.tags && product.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {product.tags.slice(0, 2).map((tag: any) => (
                      <span
                        key={tag.id}
                        className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAddToCart(product)
                  }}
                  disabled={isAdding || product.stock === 0}
                  className="mt-2 w-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 py-2 rounded-sm text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={16} />
                  {isAdding ? "Adding..." : "Add to Cart"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
